import React, { useContext } from "react";
import { getArticle, queryArticles } from "../../src/graphql/queries";

import ArticleFilterBar from "../../src/components/Content/Article/ArticleFilterBar";
import ArticlePage from "../../src/components/Content/Article/ArticlePage";
import Articles from "../../src/components/Content/Article/Articles";
import MainLayout from "../../src/components/Layout/MainLayout";
import SeoHead from "../../src/components/Seo/SeoHead";
import { TheCycleverseTheme } from "src/theme";
import { default as categoriesConfig } from "../../src/categoriesArticles.json";
import { createFetcher } from "src/lib/fetcher";
import environment from "../../src/environment";
import { locales } from "../../i18n";
import { makeStyles } from "@material-ui/core";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  blogContent: theme.blogClasses.blogContent,
}));

const Page = (props) => {
  const { prefetchedArticle, prefetchedArticleList } = props;
  const classes = useStyles();
  const router = useRouter();
  const { generic } = router.query;
  const { t } = useTranslation("common");
  const category = Array.isArray(generic) ? generic[0] : generic;
  const isCategory = categoriesConfig.categories.includes(category);

  if (isCategory) {
    const categoryKey = category.replace("-", "_");
    return (
      <MainLayout
        searchBar="blogSticky"
        contentClassName={classes.blogContent}
        menuComponent={
          <ArticleFilterBar
            activeCategory={category}
            categories={categoriesConfig.categoriesInBlog}
            articleGroup="blog"
          />
        }
      >
        <SeoHead
          title={t(`articleCategory_${categoryKey}_title`)}
          description={t(`articleCategory_${categoryKey}_short`)}
          ogDescription={t(`articleCategory_${categoryKey}_short`)}
        />
        <Articles prefetchedData={prefetchedArticleList} defaultCategory={category} articleGroup="blog" />
      </MainLayout>
    );
  } else {
    return (
      <MainLayout searchBar="blogSticky" contentClassName={classes.blogContent}>
        <ArticlePage
          id={generic}
          prefetchedArticle={prefetchedArticle}
          forceRerenderTimeout={environment.name == "dev" ? 500 : null}
        />
      </MainLayout>
    );
  }
};

const getStaticPropsCategory = async ({ params, locale }) => {
  const category = params.generic;
  const fetcher = createFetcher();
  const data = await fetcher([
    queryArticles,
    JSON.stringify({
      action: "search",
      categories:
        category == "all"
          ? categoriesConfig.categoriesInBlog
          : category == "allapps"
          ? categoriesConfig.categoriesInApps
          : [category],
      visibility: "public",
      size: 100,
      locale,
    }),
  ]);

  const articles = data && data.queryArticles.items;
  return {
    props: {
      prefetchedArticleList: articles,
      key: category, // force state reset for dynamic route
    },
    revalidate: environment.features.prerenderArticles ? 120 : 1,
  };
};

const getStaticPathsCategory = async () => {
  const paths = categoriesConfig.categoriesInBlog.flatMap((category) => {
    return locales.map((locale) => {
      return {
        params: {
          category,
          generic: category,
        },
        locale,
      };
    });
  });

  return environment.features.prerenderArticleCategories ? paths : [];
};

const getStaticPathsArticle = async () => {
  const fetcher = createFetcher();
  const data = await fetcher([
    queryArticles,
    JSON.stringify({
      action: "search",
      visibility: "public",
      size: 1000,
    }),
  ]);
  const allArticles = data && data.queryArticles ? data.queryArticles.items : [];
  const articles = allArticles.filter((article) => article.locale == "de");

  const paths = articles.map((article) => {
    return {
      params: {
        id: article.id,
        generic: article.id,
      },
      locale: article.locale,
    };
  });
  return paths;
};

const getStaticPropsArticle = async ({ params }) => {
  const fetcher = createFetcher();
  const data = await fetcher([getArticle, JSON.stringify({ id: params.generic })]);
  const article = data && data.getArticle;
  if (!article) {
    return {
      props: {},
      revalidate: environment.features.prerenderArticles ? 120 : 1,
    };
  }
  return {
    props: {
      prefetchedArticle: article,
      canonicalPath: `/${article.locale}/blog/${article.id}`,
      key: params.generic, // force state reset for dynamic route
    },
    revalidate: environment.features.prerenderArticles ? 120 : 1,
  };
};

export async function getStaticPaths() {
  const categoryPaths = await getStaticPathsCategory();
  const articlePaths = await getStaticPathsArticle();
  return {
    paths: [...categoryPaths, ...articlePaths],
    fallback: "blocking",
  };
}

export async function getStaticProps({ locale, params }) {
  const isCategory = params && params.generic && categoriesConfig.categories.includes(params.generic);
  if (isCategory) {
    return await getStaticPropsCategory({ params, locale });
  } else {
    return await getStaticPropsArticle({ params });
  }
}

export default Page;
